/**
 * Deal, BOM and Quote lines (piece 18): the 6/15 stage model as seeded
 * reference data, sub-stage applicability by deal type and route, the
 * derived next action, the stage-completion guard, the stage weight and the
 * stagnation thresholds. 🔨 Nothing here keys on a stage's ordinal position:
 * every rule names the sub-stage (decision 0205).
 */
import { roundHalfUpCents } from '../money';
import { counted } from '../composites/countedUnit';
import { parseDiscoveryKey } from './discovery';
import { DAY_MS } from '../zonedTime';

export const DEAL_TYPES = ['Project', 'Parts', 'Service'] as const;
export type DealType = (typeof DEAL_TYPES)[number];

export const DEAL_ORIGINS = ['Pipelined', 'QuickBooked', 'Emergency', 'ServiceRequest'] as const;
export type DealOrigin = (typeof DEAL_ORIGINS)[number];

/** 🔒 Discovery stands between Intent and Evaluation since 2026-10-18 (decision 5450, the owner's answer M1b). */
export const DEAL_STAGES = ['Intent', 'Discovery', 'Evaluation', 'Negotiation', 'Decision', 'Lost', 'Won'] as const;
export type DealStage = (typeof DEAL_STAGES)[number];

/**
 * 🔒 The part a person plays in the buying decision (decision 0618), drawn as a
 * pill beside their name on the Intent card's Authority row. Five fixed values:
 * a second configurable role list beside `DealTeamRoleType` buys nothing.
 */
export const DEAL_AUTHORITY_ROLES = ['DecisionMaker', 'Influencer', 'Champion', 'Gatekeeper', 'Other'] as const;
export type DealAuthorityRole = (typeof DEAL_AUTHORITY_ROLES)[number];

/** 🔨 A picker offers words and stores a member: `DecisionMaker` is a column spelling, not language. */
export const DEAL_AUTHORITY_ROLE_LABELS: Readonly<Record<DealAuthorityRole, string>> = {
  DecisionMaker: 'Decision maker',
  Influencer: 'Influencer',
  Champion: 'Champion',
  Gatekeeper: 'Gatekeeper',
  Other: 'Other',
};

/** The stable keys automation names, never display strings (decision 0205). */
export const SUB_STAGES = [
  'Budget', 'Authority', 'Need', 'Timing',
  'ShownToProspect', 'ProposalReviewed', 'BOMBuilt', 'RFQIssued',
  'NegotiateTerms', 'SubmitQuote',
  'IssueSalesOrder', 'CreateWorkOrder', 'WorkInProgress', 'Billed', 'PaymentReceived',
] as const;
export type SubStage = (typeof SUB_STAGES)[number];

export interface SubStageDefinitionSeed {
  stage: DealStage;
  subStage: SubStage | null;
  shortName: string;
  cardQuestion: string;
  sortOrder: number;
  probability: number;
  appliesToDealTypes: readonly DealType[];
  /**
   * 🔒 The row's answer is an uploaded file rather than a typed note (decision
   * 0595, taking up what decision 0332 left to the piece that asked for it).
   * Negotiate Terms is the one sub-stage that carries it, and the flag is
   * seeded reference data, so another row gains it without a release.
   */
  takesFile?: boolean;
}

const ALL: readonly DealType[] = DEAL_TYPES;

/** The stage-level weights proposed in the piece's open issue: Intent 10, Evaluation 30, Negotiation 60, Decision 80, Won 100, Lost 0; Discovery 20 since decision 5450. */
export const STAGE_WEIGHTS: Readonly<Record<DealStage, number>> = { Intent: 10, Discovery: 20, Evaluation: 30, Negotiation: 60, Decision: 80, Lost: 0, Won: 100 };

/**
 * The seed: fifteen sub-stage rows plus one stage-level row per stage, each
 * carrying a short name, the StageCard question and a probability. A
 * sub-stage inherits its stage's weight until the product owner sets its own;
 * the eleven questions the spec did not write are drafted from the short
 * names for the product owner to correct.
 */
export const SUB_STAGE_SEED: readonly SubStageDefinitionSeed[] = [
  { stage: 'Intent', subStage: null, shortName: 'Intent', cardQuestion: 'Intent', sortOrder: 100, probability: 10, appliesToDealTypes: ALL },
  { stage: 'Intent', subStage: 'Budget', shortName: 'Budget', cardQuestion: 'The prospect has a budget for this work', sortOrder: 110, probability: 10, appliesToDealTypes: ALL },
  { stage: 'Intent', subStage: 'Authority', shortName: 'Authority', cardQuestion: 'We know who asked for the work and can sign for it', sortOrder: 120, probability: 10, appliesToDealTypes: ALL },
  { stage: 'Intent', subStage: 'Need', shortName: 'Need', cardQuestion: 'The need is stated in the prospect’s own words', sortOrder: 130, probability: 10, appliesToDealTypes: ALL },
  { stage: 'Intent', subStage: 'Timing', shortName: 'Timing', cardQuestion: 'The prospect has said when they need it', sortOrder: 140, probability: 10, appliesToDealTypes: ALL },
  // 🔒 Decision 5450: Discovery's stage-level row; the stage carries no sub-stage rows, so its card reads 0/0 and its body is the Discovery card.
  { stage: 'Discovery', subStage: null, shortName: 'Discovery', cardQuestion: 'The visit has gathered what the proposal needs', sortOrder: 150, probability: 20, appliesToDealTypes: ALL },
  { stage: 'Evaluation', subStage: null, shortName: 'Evaluation', cardQuestion: 'Evaluation', sortOrder: 200, probability: 30, appliesToDealTypes: ALL },
  { stage: 'Evaluation', subStage: 'ShownToProspect', shortName: 'Shown to Prospect', cardQuestion: 'Product/service has been shown', sortOrder: 210, probability: 30, appliesToDealTypes: ALL },
  { stage: 'Evaluation', subStage: 'ProposalReviewed', shortName: 'Proposal Reviewed', cardQuestion: 'Proposal has been reviewed with the prospect', sortOrder: 220, probability: 30, appliesToDealTypes: ALL },
  { stage: 'Evaluation', subStage: 'BOMBuilt', shortName: 'BOM Built', cardQuestion: 'BOM built', sortOrder: 230, probability: 30, appliesToDealTypes: ALL },
  { stage: 'Evaluation', subStage: 'RFQIssued', shortName: 'RFQ Issued', cardQuestion: 'RFQs sent to vendors', sortOrder: 240, probability: 30, appliesToDealTypes: ALL },
  { stage: 'Negotiation', subStage: null, shortName: 'Negotiation', cardQuestion: 'Negotiation', sortOrder: 300, probability: 60, appliesToDealTypes: ALL },
  { stage: 'Negotiation', subStage: 'NegotiateTerms', shortName: 'Negotiate Terms', cardQuestion: 'Terms have been negotiated with the prospect', sortOrder: 310, probability: 60, appliesToDealTypes: ALL, takesFile: true },
  { stage: 'Negotiation', subStage: 'SubmitQuote', shortName: 'Submit Quote', cardQuestion: 'The Quote has been sent to the prospect', sortOrder: 320, probability: 60, appliesToDealTypes: ALL },
  { stage: 'Decision', subStage: null, shortName: 'Decision', cardQuestion: 'The prospect decides: Won or Lost', sortOrder: 400, probability: 80, appliesToDealTypes: ALL },
  { stage: 'Lost', subStage: null, shortName: 'Lost', cardQuestion: 'Lost', sortOrder: 500, probability: 0, appliesToDealTypes: ALL },
  { stage: 'Won', subStage: null, shortName: 'Won', cardQuestion: 'Won', sortOrder: 600, probability: 100, appliesToDealTypes: ALL },
  { stage: 'Won', subStage: 'IssueSalesOrder', shortName: 'Issue Sales Order', cardQuestion: 'A Sales Order exists', sortOrder: 610, probability: 100, appliesToDealTypes: ALL },
  { stage: 'Won', subStage: 'CreateWorkOrder', shortName: 'Create Work Order', cardQuestion: 'A Work Order exists', sortOrder: 620, probability: 100, appliesToDealTypes: ['Project', 'Service'] },
  { stage: 'Won', subStage: 'WorkInProgress', shortName: 'Work in Progress', cardQuestion: 'The Work Order is in progress', sortOrder: 630, probability: 100, appliesToDealTypes: ['Project', 'Service'] },
  { stage: 'Won', subStage: 'Billed', shortName: 'Billed', cardQuestion: 'An Invoice has been raised', sortOrder: 640, probability: 100, appliesToDealTypes: ALL },
  { stage: 'Won', subStage: 'PaymentReceived', shortName: 'Payment Received', cardQuestion: 'Payment has landed', sortOrder: 650, probability: 100, appliesToDealTypes: ALL },
];

export function stageOf(subStage: SubStage): DealStage {
  return SUB_STAGE_SEED.find((row) => row.subStage === subStage)!.stage;
}

export function subStagesOf(stage: DealStage): SubStage[] {
  return SUB_STAGE_SEED.filter((row) => row.stage === stage && row.subStage !== null).map((row) => row.subStage!);
}

/** The five Won sub-stages are system-driven side effects of real documents, never a checkbox a person ticks. */
export const SYSTEM_DRIVEN_SUB_STAGES: readonly SubStage[] = ['IssueSalesOrder', 'CreateWorkOrder', 'WorkInProgress', 'Billed', 'PaymentReceived'];

/** The sub-stages a Quick Deal seeds not applicable; Authority, Need and BOM Built stay applicable and are the three a quick deal answers. */
export const QUICK_DEAL_NOT_APPLICABLE: readonly SubStage[] = ['Budget', 'Timing', 'ShownToProspect', 'ProposalReviewed', 'NegotiateTerms', 'SubmitQuote'];

/**
 * 🔒 The sub-stages a service request answers (decisions 1912, 1990): Authority and
 * Need, who asked and the problem in their words, and the four Won rows its job,
 * its invoice and its payment tick. Every other row is seeded not applicable, Issue
 * Sales Order among them, because no Sales Order stands behind a request's job.
 */
export const SERVICE_REQUEST_APPLICABLE: readonly SubStage[] = ['Authority', 'Need', 'CreateWorkOrder', 'WorkInProgress', 'Billed', 'PaymentReceived'];

/**
 * 🔨 The rows never auto-marked not applicable: who asked and the need. BOM Built
 * left this list with decision 2511, because a Parts or Service deal carries no
 * bill of materials and its Quote is the list.
 */
export const NEVER_NOT_APPLICABLE: readonly SubStage[] = ['Authority', 'Need'];

/**
 * 🔒 Whether a Deal's create writes its first bill of materials (decision 2511,
 * amending 0039 and 1090): a Project's form adds one, and a Parts or Service
 * deal creates none, its Quote being the list. Any Deal adds one on purpose from
 * its Bill of materials tile.
 */
export function createsFirstList(dealType: DealType): boolean {
  return dealType === 'Project';
}

/** The default route by type: Parts and Service default to Quick, Project to Pipelined, each with a visible escape to the other. */
export function defaultRoute(dealType: DealType): 'Quick' | 'Pipelined' {
  return dealType === 'Project' ? 'Pipelined' : 'Quick';
}

/**
 * 🔒 The origin the New Deal form starts on for a chosen Type (decision 10740; T1015), `defaultRoute` in the
 * stored word: Parts and Service start as a Quick Deal, a Project on the pipeline.
 */
export function defaultOrigin(dealType: DealType): 'QuickBooked' | 'Pipelined' {
  return defaultRoute(dealType) === 'Quick' ? 'QuickBooked' : 'Pipelined';
}

/**
 * Whether a sub-stage defaults to applicable for a deal type and route,
 * reading `appliesToDealTypes` on the definition row (decision 0205) and the
 * Quick Deal seed. Deriving applicability wrongly is worse than asking, so
 * every other sub-stage defaults to applicable and a person switches it off.
 */
export function defaultApplicable(subStage: SubStage, dealType: DealType, origin: DealOrigin, definitions: readonly SubStageDefinitionSeed[] = SUB_STAGE_SEED, carriesList = true): boolean {
  const row = definitions.find((d) => d.subStage === subStage);
  if (!row) return false;
  // 🔒 BOM Built is not applicable on a Deal that carries no bill of materials (decision 2511): a Parts or Service deal's Quote is its list.
  if (subStage === 'BOMBuilt' && !carriesList) return false;
  if (!row.appliesToDealTypes.includes(dealType)) return false;
  if (origin === 'QuickBooked' && QUICK_DEAL_NOT_APPLICABLE.includes(subStage)) return false;
  // 🔒 A request builds no list and walks no pipeline (decisions 1912, 1990): Authority, Need and the Won rows its job moves stay applicable.
  // 🔒 An emergency is the Service form with Emergency on, so it seeds its rows as a request's, Issue Sales Order not applicable (decision 2294; T1721).
  if (origin === 'ServiceRequest' || origin === 'Emergency') return SERVICE_REQUEST_APPLICABLE.includes(subStage);
  return true;
}

export interface SubStageState {
  subStage: SubStage;
  applicable: boolean;
  completedAt?: Date | string | null;
  dueDate?: Date | string | null;
  notes?: string | null;
  /**
   * 🔒 The row is answered by a list the Deal carries (decision 5492): the Authority row with one
   * or more tagged people. The stage guard never refuses it for an unticked box or a blank note.
   */
  listAnswered?: boolean;
}

/**
 * RFQ Issued requires BOM Built where the Deal carries a bill of materials:
 * marking BOM Built not applicable makes RFQ Issued not applicable too, and
 * marking RFQ Issued applicable requires BOM Built to be applicable. The edge
 * constrains applicability both ways.
 *
 * 🔒 A Deal carrying no bill of materials is the exception (decision 2511): BOM
 * Built is not applicable there and may stay so, and RFQ Issued stands on the
 * Quote's lines, so neither half of the edge holds.
 */
export function applicabilityProblem(rows: readonly SubStageState[], change: { subStage: SubStage; applicable: boolean }, carriesList = true): string | null {
  // 🔒 Who asked and the need are never marked not applicable, on any Deal (T1005; decision 10690).
  if (!change.applicable && NEVER_NOT_APPLICABLE.includes(change.subStage)) return `${SUB_STAGE_SEED.find((d) => d.subStage === change.subStage)?.shortName ?? change.subStage} is always applicable; it cannot be marked not applicable`;
  if (!carriesList) return null;
  if (change.subStage === 'BOMBuilt' && !change.applicable) return 'BOM Built is not applicable only on a Deal with no bill of materials; this Deal carries one';
  if (change.subStage === 'RFQIssued' && change.applicable) {
    const bom = rows.find((r) => r.subStage === 'BOMBuilt');
    if (bom && !bom.applicable) return 'RFQ Issued needs BOM Built to be applicable; there is nothing to send a request against';
  }
  return null;
}

/** The rows that follow from a change: RFQ Issued drops to not applicable when BOM Built does, on a Deal carrying a bill of materials (decision 2511). */
export function applicabilityCascade(change: { subStage: SubStage; applicable: boolean }, carriesList = true): { subStage: SubStage; applicable: boolean }[] {
  if (carriesList && change.subStage === 'BOMBuilt' && !change.applicable) return [{ subStage: 'RFQIssued', applicable: false }];
  return [];
}

/** 🔨 Next action is a list derived from the incomplete applicable sub-stages of the current stage, never typed; at Decision it is the decision itself. */
export function nextActions(stage: DealStage, rows: readonly SubStageState[]): string[] {
  if (stage === 'Decision') return ['Won', 'Lost'];
  if (stage === 'Lost') return [];
  return SUB_STAGE_SEED.filter((d) => d.stage === stage && d.subStage !== null)
    .filter((d) => {
      const row = rows.find((r) => r.subStage === d.subStage);
      return row !== undefined && row.applicable && !row.completedAt;
    })
    .map((d) => d.shortName);
}

/**
 * 🔒 What each Intent row lacks when it is open, in the row's own rule (T980; decision 10690; piece 19): Budget completes on a saved Target and Timing on a saved Expected quote, so neither is asked for a tick.
 * A row not named here completes on its tick and reads "is not checked".
 */
const UNMET_RULE: Partial<Record<SubStage, string>> = { Budget: 'has no Target saved', Timing: 'has no Expected quote saved' };

/**
 * 🔒 `canCompleteStage(card)`: a stage completes only when every applicable
 * sub-stage of it is checked and carries non-empty notes; a checked box over
 * a blank note never counts. The three Mark Complete buttons call it, and a
 * Lead's gated stages call the same guard. 🔒 A row answered by a list the Deal
 * carries, the Authority row with a tagged person (decision 5492), is neither
 * unchecked nor over a blank note.
 */
export function canCompleteStage(stage: DealStage, rows: readonly SubStageState[]): { ok: boolean; problems: string[] } {
  const problems: string[] = [];
  for (const d of SUB_STAGE_SEED.filter((r) => r.stage === stage && r.subStage !== null)) {
    const row = rows.find((r) => r.subStage === d.subStage);
    if (!row || !row.applicable) continue;
    // 🔒 A tagged Authority person is the row's answer, so it needs neither the tick nor a note (decision 5492).
    if (row.listAnswered) continue;
    if (!row.completedAt) problems.push(`${d.shortName} ${UNMET_RULE[d.subStage!] ?? 'is not checked'}`);
    // 🔒 A row the system ticks off its list never blocks the next stage (decision 2091; T1189): its note is the list's, and nobody has a box to type one.
    else if (LIST_BACKED_SUB_STAGES.includes(d.subStage!)) continue;
    else if (!row.notes || row.notes.trim() === '') problems.push(`${d.shortName} is checked over a blank note`);
  }
  return { ok: problems.length === 0, problems };
}

/** 🔒 The stages a rep's Mark Complete and Back walk, in order; Discovery stands second since decision 5450. */
export const WORKING_STAGES: DealStage[] = ['Intent', 'Discovery', 'Evaluation', 'Negotiation', 'Decision'];

/** The stage a rep's Mark Complete moves the Deal to; Won and Lost are reached by their own actions. */
export function nextStage(stage: DealStage): DealStage | null {
  const order: DealStage[] = WORKING_STAGES;
  const i = order.indexOf(stage);
  return i >= 0 && i < order.length - 1 ? order[i + 1]! : null;
}

/**
 * The stage Back moves the Deal to (decision 0331): Discovery, Evaluation,
 * Negotiation and Decision each step back one (Discovery since decision 5450); Intent has nothing behind it, Won keeps
 * its own rules and Lost reopens through Reopen. Nothing is deleted on the
 * way back: the gate and its card stay, and enteredAt is stamped again.
 */
export function previousStage(stage: DealStage): DealStage | null {
  const order: DealStage[] = WORKING_STAGES;
  const i = order.indexOf(stage);
  return i > 0 ? order[i - 1]! : null;
}

/**
 * The two Evaluation rows whose answer is a list the Deal already carries
 * (decision 0332): BOM Built shows the BOM's lines and RFQ Issued the Deal's
 * RFQs, nested under the row, in place of a typed note. The note the gate
 * reads is written by the system from the list's size, and a tick over an
 * empty list is refused rather than rubber-stamped.
 */
export const LIST_BACKED_SUB_STAGES: readonly SubStage[] = ['BOMBuilt', 'RFQIssued'];

/**
 * The sub-stages whose answer is an uploaded file (decision 0595): Negotiate
 * Terms takes the negotiated terms document, the way the Lead's MSA signed row
 * takes the signed agreement. 🔨 The list is derived from the seeded
 * definition rows and this constant is what the seed writes, so a row gains
 * the flag by an edit to reference data and never by a release.
 */
export const FILE_ANSWERED_SUB_STAGES: readonly SubStage[] = SUB_STAGE_SEED.filter((r) => r.takesFile === true).map((r) => r.subStage!);

/**
 * 🔒 The rows whose notes are `Note` rows tagged to the Deal with the row's
 * sub-stage as their section key (decision 0725): every row a person types on.
 * 🔨 A list-backed row, a file-answered row and a system-driven Won row take
 * none, because their answer is a list, a file or a document.
 */
export const SECTION_NOTE_SUB_STAGES: readonly SubStage[] = SUB_STAGES.filter(
  (subStage) => !LIST_BACKED_SUB_STAGES.includes(subStage) && !FILE_ANSWERED_SUB_STAGES.includes(subStage) && !SYSTEM_DRIVEN_SUB_STAGES.includes(subStage),
);

/**
 * 🔒 The two section-note rows that complete on a saved answer rather than a
 * tick (decisions 0664, 0719): the guard reads the answer written out, and their
 * tagged notes are the row's history alone (decision 0725).
 */
export const ANSWER_NOTED_SUB_STAGES: readonly SubStage[] = ['Budget', 'Timing'];

/** Whether the stage guard reads a row's latest tagged note (decision 0725): a section-note row that completes on the tick. */
export function guardReadsSectionNote(subStage: SubStage): boolean {
  return SECTION_NOTE_SUB_STAGES.includes(subStage) && !ANSWER_NOTED_SUB_STAGES.includes(subStage);
}

/** The pill a section note carries on the Internal Notes tile: the stage and the row, such as *Intent · Need* (decision 0725). */
export function dealSectionLabel(subStage: string): string | null {
  const row = SUB_STAGE_SEED.find((d) => d.subStage === subStage);
  if (row) return `${row.stage} · ${row.shortName}`;
  // 🔒 A Discovery answer's key is `template/question` (decision 5450): its pill names the stage it was written on.
  return parseDiscoveryKey(subStage) ? 'Discovery' : null;
}

export function listBackedNote(subStage: SubStage, count: number): string {
  if (subStage === 'BOMBuilt') return counted(count, 'BOM line');
  return `${counted(count, 'RFQ')} issued`;
}

/** Why a list-backed row cannot be ticked: its list is empty. */
export function listBackedTickProblem(subStage: SubStage, count: number): string | null {
  if (!LIST_BACKED_SUB_STAGES.includes(subStage) || count > 0) return null;
  return subStage === 'BOMBuilt'
    ? 'BOM Built needs at least one line on the Bill of materials; add the lines or mark the row N/A'
    : 'RFQ Issued needs at least one RFQ sent from the Bill of materials; send one or mark the row N/A';
}

/**
 * The weighted pipeline: dealValue times the probability of the
 * highest-sortOrder completed sub-stage of the current stage; before any
 * completes, the stage's own row. Decision and Lost carry no sub-stage rows,
 * so the stage row is what gives them a weight (decision 0205).
 */
export function stageWeight(stage: DealStage, rows: readonly SubStageState[], definitions: readonly SubStageDefinitionSeed[] = SUB_STAGE_SEED): number {
  const completed = definitions
    .filter((d) => d.stage === stage && d.subStage !== null)
    .filter((d) => rows.some((r) => r.subStage === d.subStage && r.applicable && r.completedAt))
    .sort((a, b) => b.sortOrder - a.sortOrder);
  if (completed.length > 0) return completed[0]!.probability;
  return definitions.find((d) => d.stage === stage && d.subStage === null)?.probability ?? STAGE_WEIGHTS[stage];
}

/**
 * 🔒 A Deal's value times a probability, in whole cents, rounded once half up (T2192 item 10). The Deal page, the
 * Open Pipeline report and the Sales Reporting Dashboard each call this, so a deal reads one weighted figure
 * everywhere, from the same sub-stage probability.
 */
export function weightedCents(dealValueCents: number, probabilityPercent: number): number {
  return roundHalfUpCents((dealValueCents * probabilityPercent) / 100);
}

export function weightedValueCents(dealValueCents: number, stage: DealStage, rows: readonly SubStageState[], definitions?: readonly SubStageDefinitionSeed[]): number {
  return weightedCents(dealValueCents, stageWeight(stage, rows, definitions));
}

// ---- Stagnation ----

export interface ThresholdSeed {
  stage: DealStage;
  subStage: SubStage | null;
  thresholdDays: number;
  escalationThresholdDays: number | null;
  escalatesToManager: boolean;
  isActive: boolean;
}

export const FOLLOW_UP_DEFAULT_DAYS = 30;

/**
 * Fifteen sub-stage rows and Decision's row at stage level, sixteen in all,
 * seeded at the Follow-Up Rules default of 30 days. Work in Progress seeds
 * the toggle off, Negotiate Terms carries the manager escalation, and
 * Decision alone carries the second threshold. 🔨 Lost is terminal and has no
 * row at all.
 */
export const THRESHOLD_SEED: readonly ThresholdSeed[] = [
  ...SUB_STAGES.map((subStage): ThresholdSeed => ({
    stage: stageOf(subStage),
    subStage,
    thresholdDays: FOLLOW_UP_DEFAULT_DAYS,
    escalationThresholdDays: null,
    escalatesToManager: subStage === 'NegotiateTerms',
    isActive: subStage !== 'WorkInProgress',
  })),
  { stage: 'Decision', subStage: null, thresholdDays: 14, escalationThresholdDays: 28, escalatesToManager: true, isActive: true },
];

export interface StagnationVerdict {
  stale: boolean;
  days: number;
  escalate: boolean;
}


/**
 * A sub-stage threshold counts from `DealSubStage.lastMovedAt`; Decision's
 * from `DealStageGate.enteredAt`. A not-applicable sub-stage, a completed one,
 * an inactive threshold, a Lost deal, an Emergency deal and a service request are
 * never stale (decision 1912).
 */
export function stagnation(input: { stage: DealStage; origin: DealOrigin; subStage: SubStage | null; applicable: boolean; completed: boolean; since: Date; threshold: ThresholdSeed | null }, now: Date): StagnationVerdict {
  const days = Math.floor((now.getTime() - input.since.getTime()) / DAY_MS);
  const never = input.stage === 'Lost' || input.origin === 'Emergency' || input.origin === 'ServiceRequest' || !input.applicable || input.completed || !input.threshold || !input.threshold.isActive;
  if (never) return { stale: false, days, escalate: false };
  const stale = days >= input.threshold!.thresholdDays;
  const escalateAt = input.threshold!.escalationThresholdDays ?? (input.threshold!.escalatesToManager ? input.threshold!.thresholdDays : null);
  return { stale, days, escalate: stale && escalateAt !== null && days >= escalateAt };
}

/** Pipeline metrics exclude QuickBooked and ServiceRequest (decision 1912); stagnation and cycle time exclude Emergency too. Revenue and commission include every origin. */
export function inPipelineMetrics(origin: DealOrigin): boolean {
  return origin === 'Pipelined';
}
